import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, PublicIcon, ShareIcon, SolidEyeIcon, StreamrNewLogoSolo, } from '@streamr/tv-ui';
import { useRef } from 'react';
import styled from 'styled-components';
import { BroadcastStateService } from '../../services/broadcaststate/BroadcastStateService';
import { DestinationsService } from '../../services/destinations/DestinationsService';
import { ViewerCountService } from '../../services/viewercount/ViewerCountService';
import { copyToClipboard } from '../../utils/copyToClipboard';
import { formBroadcastUrl } from '../../utils/url';
import { showDestinationsPopup } from '../popups/destinations/DestinationsPopup';
import { LinkedInIcon, TwitchIcon, XIcon, YouTubeIcon, } from '../popups/destinations/PlatformLogos';
import { showToast } from '../popups/ToastPopup';
import { LogoLink } from '../shared/LogoLink';
const PLATFORM_ICONS = {
    youtube: _jsx(YouTubeIcon, { width: "16", height: "16" }),
    twitch: _jsx(TwitchIcon, { width: "16", height: "16" }),
    x: _jsx(XIcon, { width: "16", height: "16" }),
    linkedin: _jsx(LinkedInIcon, { width: "16", height: "16" }),
};
export function BroadcasterHeader({ isBroadcasting, showViewerCount = true, }) {
    const destinationsButtonRef = useRef(null);
    const activeBroadcast = BroadcastStateService.getOutput().useRequiredActiveBroadcast();
    const broadcastUrl = formBroadcastUrl(activeBroadcast);
    const viewerCount = ViewerCountService.getOutput().useViewerCountForActiveBroadcast();
    const destinations = DestinationsService.getOutput().useDestinations();
    const onAirPlatforms = Object.entries(destinations).filter(([, state]) => state &&
        typeof state === 'object' &&
        'status' in state &&
        state.status === 'publishing');
    return (_jsxs(HeaderRoot, { children: [_jsxs(LeftSide, { children: [_jsx(StyledLogoLink, { logoImage: _jsx(StreamrNewLogoSolo, {}) }), _jsxs(BadgeGroup, { children: [_jsxs(TitleBadge, { children: [isBroadcasting && _jsx(LivePill, { children: "Live" }), onAirPlatforms.length > 0 && (_jsx(OnAirIcons, { children: onAirPlatforms.map(([id]) => (_jsx(OnAirIcon, { children: PLATFORM_ICONS[id] }, id))) })), _jsx(SessionName, { children: activeBroadcast.name || '\u200B' })] }), isBroadcasting &&
                                viewerCount !== undefined &&
                                showViewerCount && (_jsxs(ViewerBadge, { children: [_jsx(SolidEyeIcon, {}), _jsx("span", { children: viewerCount || 0 })] }))] })] }), _jsxs(RightSide, { children: [_jsx("div", { ref: destinationsButtonRef, children: _jsx(IconButton, { className: "tv-ui-shiny-border", onClick: () => {
                                showDestinationsPopup({
                                    anchorRef: destinationsButtonRef,
                                    variant: 'desktop',
                                });
                            }, children: _jsx(PublicIcon, {}) }) }), _jsx(IconButton, { className: "tv-ui-shiny-border", onClick: () => {
                            copyToClipboard(broadcastUrl);
                            showToast('Link copied!');
                        }, children: _jsx(ShareIcon, {}) })] })] }));
}
const HeaderRoot = styled.div `
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
`;
const LeftSide = styled.div `
    display: flex;
    align-items: center;
    gap: 1rem;
    min-width: 0;
`;
const StyledLogoLink = styled(LogoLink) `
    --tv-LogoLink-height: 2rem;
    flex-shrink: 0;

    svg {
        height: 2rem;
        width: 2rem;
    }
`;
const BadgeGroup = styled.div `
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
`;
const TitleBadge = styled.div `
    display: flex;
    align-items: center;
    gap: 0.5rem;
    height: 2rem;
    padding: 0.25rem 0.75rem 0.25rem 0.25rem;
    border-radius: 0.5rem;
    backdrop-filter: blur(50px);
    background: rgba(128, 128, 128, 0.3);
    min-width: 0;
`;
const LivePill = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1.5rem;
    padding: 0 0.375rem;
    border-radius: 0.375rem;
    background: var(--tv-ui-color-6);
    color: white;
    font-family: 'Poppins', sans-serif;
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1.25rem;
    flex-shrink: 0;
`;
const OnAirIcons = styled.div `
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
`;
const OnAirIcon = styled.div `
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.125rem;
    height: 1.125rem;

    svg {
        width: 1rem;
        height: 1rem;
    }
`;
const SessionName = styled.span `
    font-family: 'Poppins', sans-serif;
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1.25rem;
    color: white;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
`;
const ViewerBadge = styled.div `
    display: flex;
    align-items: center;
    gap: 0.25rem;
    height: 2rem;
    padding: 0.375rem 0.5rem;
    border-radius: 0.5rem;
    backdrop-filter: blur(50px);
    background: rgba(152, 152, 157, 0.2);
    color: var(--tv-ui-color-3);
    font-family: 'Poppins', sans-serif;
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 1.25rem;
    flex-shrink: 0;

    svg {
        width: 1rem;
        height: 1rem;
    }
`;
const RightSide = styled.div `
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
`;
const IconButton = styled(Button) `
    --Button_Background: rgba(47, 47, 47, 0.6);
    --Button_BorderRadius: 0.75rem;
    --Button_Height: 2.75rem;
    --tv-ui-shiny-border-thickness: 0.0625rem;
    --tv-ui-shiny-border-radius: var(--Button_BorderRadius);

    color: var(--tv-ui-color-3);
    min-width: 2.75rem;
`;
